import { useCallback } from 'react';
import { create } from 'zustand';
import { getTriggerTypes } from 'services/asset';
import { unionBy } from 'lodash';

// ======================
// Types
// ======================

export type CalculationType = 'COUNTER_BASED' | 'DATE_BASED';

// Reserved code for the one trigger type that gets the Sub Type dropdown
// instead of the Calculation Type field. Matched by code, not by display name.
export const RUNTIME_TRIGGER_TYPE_CODE = 'RUNTIME';

export interface TriggerTypeOption {
  label: string;
  value: string; // trigger type id, stringified — matches the id-string
  // convention used by other id-backed form fields (e.g. `manufacture`, `type`)
  code: string;
  calculationType: CalculationType;
}

interface RawTriggerType {
  id: number;
  name: string;
  code: string;
  calculationType: CalculationType;
}

// A maintenance schedule's `maintenanceTriggerTypeId` is submitted as just the
// trigger type id (a plain number, also what GET responses return for this
// same field — GET separately includes a populated `maintenanceTriggerType:
// { id, code, name, calculationType }` relation for display purposes only).
// Normalize to the id-as-string used throughout form state, tolerating an
// object shape too in case one is ever passed in directly (e.g. from the
// `maintenanceTriggerType` relation). Mirrors normalizeTriggerSubTypeId.
export const normalizeTriggerTypeId = (
  raw: { id: number } | number | string | null | undefined
): string | null => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') return String(raw.id);
  return String(raw);
};

interface TriggerTypeStore {
  triggerTypes: TriggerTypeOption[];
  setTriggerTypes: (types: TriggerTypeOption[]) => void;
}

// ======================
// Store
// ======================

export const useTriggerTypeStore = create<TriggerTypeStore>((set) => ({
  triggerTypes: [],
  setTriggerTypes: (triggerTypes) => set({ triggerTypes })
}));

// ======================
// Helpers
// ======================

// mirrors mapTriggerSubTypeResponse in triggerSubType.tsx
// API returns { id, name, code, calculationType }
// label = name, value = id (stringified) — saved schedule submits
// maintenance_trigger_type_id as just the id
const mapTriggerTypeResponse = (raw: RawTriggerType[]): TriggerTypeOption[] => {
  return raw.map((item) => ({
    label: item.name,
    value: String(item.id),
    code: item.code,
    calculationType: item.calculationType
  }));
};

// ======================
// Hooks
// ======================

/**
 * Hook to fetch trigger types — mirrors useFetchTriggerSubTypes
 */
export const useFetchTriggerTypes = () => {
  const { setTriggerTypes } = useTriggerTypeStore();

  const init = useCallback(async () => {
    try {
      const data = await getTriggerTypes();
      setTriggerTypes(mapTriggerTypeResponse(data.maintenanceTriggerTypes));
    } catch (error) {
      console.error('Failed to fetch trigger types:', error);
    }
  }, [setTriggerTypes]);

  return init;
};

/**
 * Hook to force-insert a trigger type into the store during edit
 * mirrors useInsertOptionTriggerSubType
 */
export const useInsertOptionTriggerType = () => {
  const insert = useCallback((option: TriggerTypeOption) => {
    const { triggerTypes, setTriggerTypes } = useTriggerTypeStore.getState();
    const updated = unionBy([option], triggerTypes, 'value');
    setTriggerTypes(updated);
  }, []);

  return insert;
};

// ======================
// Plain lookups (usable outside React — validation schemas, payload builders)
// ======================

// Zustand stores expose `.getState()` outside of components, so these don't
// need to be hooks and don't need a `triggerTypes` param threaded in.
export const getTriggerTypeById = (
  triggerTypeId: string | number | null | undefined
): TriggerTypeOption | undefined => {
  if (triggerTypeId == null || triggerTypeId === '') return undefined;
  const { triggerTypes } = useTriggerTypeStore.getState();
  return triggerTypes.find((t) => t.value === String(triggerTypeId));
};

export const isRuntimeTriggerType = (triggerTypeId: string | number | null | undefined): boolean =>
  getTriggerTypeById(triggerTypeId)?.code === RUNTIME_TRIGGER_TYPE_CODE;
